import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../../firebase";
import { resolvePublicCheckout } from "../../utility/publicCheckout";
import { ticketingDashboardRequest } from "../../utility/ticketingDashboard";
import "./publicCheckoutIncidents.scss";
export default function PublicCheckoutIncidents({ eventId, org, refreshKey = 0 }) {
  const { t } = useTranslation("ticketingDashboard");
  const [cases, setCases] = useState([]),
    [error, setError] = useState(false),
    [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({}),
    [participantId, setParticipantId] = useState(""),
    [sessionId, setSessionId] = useState("");
  const [reload, setReload] = useState(0);
  const [cursors, setCursors] = useState([null]), [page, setPage] = useState(0);
  const [nextCursor, setNextCursor] = useState(null), [loading, setLoading] = useState(false);
  useEffect(() => { setPage(0); setCursors([null]); setCases([]); }, [eventId, org?.id]);
  const cursor = cursors[page] || null;
  const admins = Array.isArray(org?.adminUid) ? org.adminUid : [org?.adminUid];
  const canManage = Boolean(
    auth.currentUser &&
      (org?.uid === auth.currentUser.uid || admins.includes(auth.currentUser.uid)),
  );
  useEffect(() => {
    let active = true;
    if (!eventId || !org?.id) return undefined;
    setCases([]); setNextCursor(null);
    const load = async () => {
      setLoading(true);
      try {
        const result = await ticketingDashboardRequest('incidents', { eventId, orgId: org.id, cursor });
        if (active) { setCases(result.items); setNextCursor(result.nextCursor); setError(false); }
      } catch (failure) { if (active) { setError(true);  } }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [eventId, org?.id, cursor, reload, refreshKey]);
  const act = async (id, action, extra = {}) => {
    if (busy) return;
    setBusy(id || "recover");
    setError(false);
    try {
      await resolvePublicCheckout({ eventId, participantId: id, action, ...extra });
      setReload((value) => value + 1);
    } catch {
      setError(true);
    } finally {
      setBusy("");
    }
  };
  if (!cases.length && !canManage && !error) return null;
  return (
    <section className="public-checkout-incidents" aria-label={t("publicCheckout.title")}>
      <div className="public-checkout-incidents__heading">
        <h3>{t("publicCheckout.title")}</h3>
        {cases.length > 0 && (
          <span>{cases.filter((person) => person.fulfillmentStatus === "blocked").length}</span>
        )}
      </div>
      {error && (
        <p role="alert">
          {t("publicCheckout.error")}{" "}
          <button type="button" onClick={() => setReload((value) => value + 1)}>
            {t("publicCheckout.refresh")}
          </button>
        </p>
      )}
      {loading && <p role="status">{t('performance.loading')}</p>}
      {cases.map((person) => (
        <article key={person.id}>
          <div className="public-checkout-incidents__summary">
            <strong>
              {person.fullName ||
                person.details?.find((field) => field.name === "Email")?.value ||
                person.id}
            </strong>
            <span>{(Number(person.paymentReceivedCents || 0) / 100).toFixed(2)} €</span>
          </div>
          <p>
            {t(
              person.fulfillmentStatus === "resolved_manual"
                ? "publicCheckout.closed"
                : "publicCheckout.blocked",
            )}
          </p>
          <small>
            {person.id} · {person.stripeSessionId}
          </small>
          <p>
            {(person.fulfillmentIssues || [])
              .map((item) => [item.name, item.option].filter(Boolean).join(" — "))
              .filter(Boolean)
              .join(", ")}
          </p>
          {person.publicNotifications?.organizer?.error && (
            <p role="status">{t("publicCheckout.notificationError")}</p>
          )}
          {person.resolutionNote && <p>{person.resolutionNote}</p>}
          {canManage && person.fulfillmentStatus === "blocked" && (
            <div className="public-checkout-incidents__actions">
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => act(person.id, "retry")}
              >
                {t("publicCheckout.retry")}
              </button>
              <label htmlFor={`resolution-${person.id}`}>
                {t("publicCheckout.note")}
                <textarea
                  id={`resolution-${person.id}`}
                  maxLength={2000}
                  value={notes[person.id] || ""}
                  onChange={(event) => setNotes({ ...notes, [person.id]: event.target.value })}
                />
              </label>
              <button
                type="button"
                disabled={Boolean(busy) || !notes[person.id]?.trim()}
                onClick={() => act(person.id, "close", { note: notes[person.id] })}
              >
                {t("publicCheckout.close")}
              </button>
            </div>
          )}
        </article>
      ))}
      {(page > 0 || nextCursor) && <nav className="dashboardPagination" aria-label={t('performance.pagination')}>
        <button disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>{t('performance.previous')}</button>
        <span>{t('performance.page', { page: page + 1 })}</span>
        <button disabled={!nextCursor || loading} onClick={() => { setCursors([...cursors.slice(0, page + 1), nextCursor]); setPage(page + 1); }}>{t('performance.next')}</button>
      </nav>}
      {canManage && (
        <details>
          <summary>{t("publicCheckout.recover")}</summary>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              act(participantId, "retry", { sessionId });
            }}
          >
            <label htmlFor="public-participant-id">
              {t("publicCheckout.participantId")}
              <input
                id="public-participant-id"
                value={participantId}
                onChange={(event) => setParticipantId(event.target.value.trim())}
                required
              />
            </label>
            <label htmlFor="public-session-id">
              {t("publicCheckout.sessionId")}
              <input
                id="public-session-id"
                value={sessionId}
                onChange={(event) => setSessionId(event.target.value.trim())}
                required
              />
            </label>
            <button type="submit" disabled={Boolean(busy)}>
              {t("publicCheckout.verify")}
            </button>
          </form>
        </details>
      )}
    </section>
  );
}
